import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Company } from '../../models/company.model';
import { Region } from 'src/app/models/region.model';
import { Feature } from 'src/app/models/feature.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RequestService } from 'src/app/services/request.service';
import { Graphic } from 'src/app/models/graphic.model';
import { MatDialog } from '@angular/material/dialog';
import { ModalDialog } from 'src/app/Modal/modal';
import { WaitForModalResult } from 'src/app/helper/modalHelper';
import { dynamicLocaleCompareWithOptions } from 'src/app/helper/sortFunctions';
import { SelectionModel } from '@angular/cdk/collections';
import { Category } from 'src/app/models/categroy.model';

@Component({
  selector: 'app-company-edit',
  templateUrl: './company-edit.component.html',
  styleUrls: ['./company-edit.component.css'],

})
export class CompanyEditComponent implements OnInit, OnDestroy {
  //Properties
  private destroyed$: Subject<void> = new Subject();
  companyForm;
  companyId: number;
  currentCompany: Company;
  companies: Company[];
  selectedCompany;
  regionList: Feature[] = [];
  featureList: Feature[] = [];
  graphicList: Graphic[] = [];
  categories: Category[];
  featureSelection = new SelectionModel<Feature>(true, []);
  regionSelection = new SelectionModel<Region>(true, []);
  seatUtilisationGraphicsSelection = new SelectionModel<Graphic>(true, []);
  baggageStorageGraphicsSelection = new SelectionModel<Graphic>(true, []);
  passengerChangeGraphicsSelection = new SelectionModel<Graphic>(true, []);
  seatUtilistationGraphics: Graphic[];
  baggageStorageGraphics: Graphic[];
  passengerChangeGraphics: Graphic[];
  regionDisplayedColumns: string[] = ['select', 'name', 'state', 'default'];
  featureDisplayedColumns: string[] = ['select'];
  backButtonModalMessage: string = "Please verify that you have saved the company, otherwise the unsaved changes will be lost!";
  canSkipCanDeactivateOnSave: boolean = false;

  company = {
    addTitel: 'Add new company',
    editTitel: 'Edit company',
    companySectionTitel: 'Company',
    contactSectionTitel: 'Contact',
    regionSectionTitel: 'Regions',
    featureSectionTitel: 'Features',
    graphicSectionTitel: 'Graphics',
    administrationSectionTitel: 'Company administration',
    companyNameLabel: 'Company name',
    companyCountryLabel: 'Country',
    companyMailDomainsLabel: 'Valid mail domains',
    companyMaxActiveUsersLabel: 'Maximum amount of active users',
    firstNameLabel: 'First name',
    lastNameLabel: 'Last name',
    phoneNumberLabel: 'Phone number',
    emailLabel: 'Email',
    buttonSave: 'Save',
    buttonBack: 'Back'
  };

  groupedGraphics: any = {
    baggage: [],
    seats: [],
    timeNeed: {
      cumulative: {
        perDoor: [],
        comparisonDoors: [],
        comparisonSelectedDoors: [],
        comparisonTravelMixes: []
      },
      total: []
    }
  };

  // Ctor
  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private requestService: RequestService) {
  }

  // Lifecycle hooks
  ngOnInit() {
    this.route.params
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (params: Params) => {
          this.companyId = +params['id'];
          this.initForm();

          this.requestService.getMasterData()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(masterData => {
              this.featureList = masterData.featureList;
              this.featureList.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));
              this.graphicList = masterData.graphicList;
              this.categories = masterData.graphicCategoryList;
              this.seatUtilistationGraphics = [];
              this.baggageStorageGraphics = [];
              this.passengerChangeGraphics = [];
              this.graphicList.forEach((graphic: Graphic) => {
                if (graphic.categoryId === 1) {
                  this.seatUtilistationGraphics.push(graphic);
                }
                else if (graphic.categoryId === 2) {
                  this.baggageStorageGraphics.push(graphic);
                } else {
                  this.passengerChangeGraphics.push(graphic);
                }
              });
              this.initGraphicStructure();
            });

          this.requestService.getRegions()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((regions: Region[]) => {
              this.regionList = regions;
              this.regionList.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));
              this.setCurrentCompany();
            });
        });
  }

  private initGraphicStructure() {
    this.groupedGraphics = {
      baggage: this.graphicList.filter(g => g.categoryId === 1),
      seats: this.graphicList.filter(g => g.categoryId === 2),
      timeNeed: {
        cumulative: {
          perDoor: this.graphicList.filter(g => g.graphicId === 9),
          comparisonDoors: this.graphicList.filter(g => [10, 11, 12].includes(g.graphicId)),
          comparisonSelectedDoors: this.graphicList.filter(g => [13, 14, 15].includes(g.graphicId)),
          comparisonTravelMixes: this.graphicList.filter(g => [16, 17, 18].includes(g.graphicId))
        },
        total: this.graphicList.filter(g => [19, 20, 21].includes(g.graphicId))
      }
    };
  }

  // Methods
  initForm() {
    this.selectedCompany = 'Select Company';
    this.companyForm = new FormGroup({
      id: new FormControl(this.companyId),
      name: new FormControl('', Validators.required),
      country: new FormControl('', Validators.required),
      contactFirstName: new FormControl('', Validators.required),
      contactLastName: new FormControl('', Validators.required),
      contactPhonenumber: new FormControl('', Validators.required),
      contactEmail: new FormControl('', [Validators.email, Validators.required]),
      features: new FormControl(null),
      regions: new FormControl(null),
      graphics: new FormControl(null),
      mailDomains: new FormControl(null),
      maxActiveUsers: new FormControl(null, [Validators.pattern(/^[0-9]\d*$/), Validators.max(255), Validators.min(1)]),
      ignoreDefaultRegion: new FormControl(false)
    });

    this.companyForm.setErrors({ required: true });
    this.regionSelection.changed
      .pipe(takeUntil(this.destroyed$))
      .subscribe(newValue => {
        this.patchRegionsToForm();
      });
    this.companyForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((newValue) => {
        if (newValue.regions != null && newValue.regions.length > 0) {
          let isDefaultRegionSet = false;
          for (let index = 0; index < newValue.regions.length; index++) {
            const element = newValue.regions[index];
            if (element.isDefaultRegion) {
              isDefaultRegionSet = true;
              break;
            }
          }

          if (!isDefaultRegionSet && !newValue.ignoreDefaultRegion) {
            this.companyForm.setErrors({ required: true });
          } else {
            this.companyForm.setErrors(null);
          }
        }
        else {
          this.companyForm.setErrors({ required: true });
        }
      });
  }

  updateForm() {
    if (this.currentCompany != null) {
      let companyName, country, firstName, lastName, phoneNumber, email, mailDomains, maxActiveUsers;

      this.selectedCompany = this.currentCompany.id;
      companyName = this.currentCompany.name ? this.currentCompany.name : '';
      country = this.currentCompany.country ? this.currentCompany.country : '';
      firstName = this.currentCompany.contactFirstName ? this.currentCompany.contactFirstName : '';
      lastName = this.currentCompany.contactLastName ? this.currentCompany.contactLastName : '';
      phoneNumber = this.currentCompany.contactPhonenumber ? this.currentCompany.contactPhonenumber : '';
      email = this.currentCompany.contactEmail ? this.currentCompany.contactEmail : '';
      mailDomains = this.currentCompany.mailDomains ? this.currentCompany.mailDomains : null;
      maxActiveUsers = this.currentCompany.maxActiveUsers ? this.currentCompany.maxActiveUsers : null;

      if (this.currentCompany.regions && this.currentCompany.regions.length > 0) {
        this.regionList.forEach((element: Region) => {
          let item = this.currentCompany.regions.find(x => x.id === element.id);
          if (item) {
            this.regionSelection.toggle(element);
          }
        });
      }

      if (this.currentCompany.features && this.currentCompany.features.length > 0) {
        this.featureList.forEach((element: Feature) => {
          let item = this.currentCompany.features.find(x => x.id === element.id);
          if (item) {
            this.featureSelection.toggle(element);
          }
        });
      }

      if (this.currentCompany.graphics && this.currentCompany.graphics.length > 0) {
        this.graphicList.forEach((graphic: Graphic) => {
          let companyGraphic = this.currentCompany.graphics.find(x => x.graphicId === graphic.graphicId);
          if (companyGraphic) {
            if (graphic.categoryId === 1) {
              this.seatUtilisationGraphicsSelection.toggle(graphic);
            }
            else if (graphic.categoryId === 2) {
              this.baggageStorageGraphicsSelection.toggle(graphic);
            } else {
              this.passengerChangeGraphicsSelection.toggle(graphic);
            }
          }
        });
      }

      this.companyForm.get("id").patchValue(this.companyId);
      this.companyForm.get("name").patchValue(companyName);
      this.companyForm.get("country").patchValue(country);
      this.companyForm.get("contactFirstName").patchValue(firstName);
      this.companyForm.get("contactLastName").patchValue(lastName);
      this.companyForm.get("contactPhonenumber").patchValue(phoneNumber);
      this.companyForm.get("contactEmail").patchValue(email);
      this.companyForm.get("features").patchValue(this.currentCompany.features);
      this.companyForm.get("regions").patchValue(this.currentCompany.regions);
      this.companyForm.get("graphics").patchValue(this.currentCompany.graphics);
      this.companyForm.get("mailDomains").patchValue(mailDomains);
      this.companyForm.get("maxActiveUsers").patchValue(maxActiveUsers);
    }
  }

  setCurrentCompany() {
    if (this.companyId > 0) {
      this.requestService.getCompany(this.companyId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(
          (resp) => {
            this.currentCompany = resp;
            this.updateForm();
          });
    }
  }

  toggleGraphic(graphic: Graphic) {
    if (graphic.categoryId === 1) {
      this.seatUtilisationGraphicsSelection.toggle(graphic);
    }
    else if (graphic.categoryId === 2) {
      this.baggageStorageGraphicsSelection.toggle(graphic);
    }
    else {
      this.passengerChangeGraphicsSelection.toggle(graphic);
    }
  }

  isGraphicSelected(graphic: Graphic) {
    if (graphic.categoryId === 1) {
      return this.seatUtilisationGraphicsSelection.selected.find(x => x.graphicId === graphic.graphicId) !== undefined;
    }
    else if (graphic.categoryId === 2) {
      return this.baggageStorageGraphicsSelection.selected.find(x => x.graphicId === graphic.graphicId) !== undefined;
    }
    else {
      return this.passengerChangeGraphicsSelection.selected.find(x => x.graphicId === graphic.graphicId) !== undefined;
    }
  }

  selectAllCategories(event) {
    if (event.checked) {
      this.baggageStorageGraphics.forEach(graphic => this.baggageStorageGraphicsSelection.select(graphic));
      this.seatUtilistationGraphics.forEach(graphic => this.seatUtilisationGraphicsSelection.select(graphic));
      this.passengerChangeGraphics.forEach(graphic => this.passengerChangeGraphicsSelection.select(graphic));
    } else {
      this.baggageStorageGraphicsSelection.clear();
      this.seatUtilisationGraphicsSelection.clear();
      this.passengerChangeGraphicsSelection.clear();
    }
  }

  graphicSelectionsHaveValue() {
    return this.seatUtilisationGraphicsSelection.hasValue() || this.baggageStorageGraphicsSelection.hasValue() || this.passengerChangeGraphicsSelection.hasValue();
  }

  selectBaggageStorageGraphics() {
    if (this.isAllBaggageStorageGraphicsSelected()) {
      this.baggageStorageGraphicsSelection.clear();
    }
    else {
      this.baggageStorageGraphics.forEach(graphic => this.baggageStorageGraphicsSelection.select(graphic));
    }
  }

  selectSeatUtilisationGraphics() {
    if (this.isAllSeatUtilisationGraphicsSelected()) {
      this.seatUtilisationGraphicsSelection.clear();
    } else {
      this.seatUtilistationGraphics.forEach(graphic => this.seatUtilisationGraphicsSelection.select(graphic));
    }
  }

  selectPassengerChangeGraphics() {
    if (this.isAllPassengerChangeGraphicsSelected()) {
      this.passengerChangeGraphicsSelection.clear();
    } else {
      this.passengerChangeGraphics.forEach(graphic => this.passengerChangeGraphicsSelection.select(graphic));
    }
  }

  isAllCategorySelected() {
    return this.isAllBaggageStorageGraphicsSelected() && this.isAllSeatUtilisationGraphicsSelected() && this.isAllPassengerChangeGraphicsSelected();
  }

  isAllBaggageStorageGraphicsSelected() {
    const numSelected = this.baggageStorageGraphicsSelection.selected.length;
    const numRows = this.baggageStorageGraphics.length;
    return numSelected === numRows;
  }

  isAllSeatUtilisationGraphicsSelected() {
    const numSelected = this.seatUtilisationGraphicsSelection.selected.length;
    const numRows = this.seatUtilistationGraphics.length;
    return numSelected === numRows;
  }

  isAllPassengerChangeGraphicsSelected() {
    const numSelected = this.passengerChangeGraphicsSelection.selected.length;
    const numRows = this.passengerChangeGraphics.length;
    return numSelected === numRows;
  }

  feeatureSelectionToggle() {
    this.isAllFeaturesSelected() ?
      this.featureSelection.clear() :
      this.featureList.forEach(row => this.featureSelection.select(row));
  }

  isAllFeaturesSelected() {
    const numSelected = this.featureSelection.selected.length;
    const numRows = this.featureList.length;
    return numSelected === numRows;
  }

  patchRegionsToForm() {
    this.companyForm.get("regions").patchValue(this.regionSelection.selected);
  }

  patchGraphicsToForm() {
    let combined = [...this.baggageStorageGraphicsSelection.selected, ...this.seatUtilisationGraphicsSelection.selected, ...this.passengerChangeGraphicsSelection.selected];
    this.companyForm.get("graphics").patchValue(combined);
  }

  patchFeaturesToForm() {
    this.companyForm.get("features").patchValue(this.featureSelection.selected);
  }

  canDeactivate() {
    if (!this.canSkipCanDeactivateOnSave) {
      const dialogRef = this.dialog.open(ModalDialog, {
        data: {
          title: 'Warning',
          message: this.backButtonModalMessage,
          showCancel: true,
          cancelButtonText: 'Cancel',
          saveButtonText: 'Leave'
        }
      });
      return WaitForModalResult(dialogRef);
    }
    else {
      return true;
    }
  }

  onSubmit() {
    this.canSkipCanDeactivateOnSave = true;
    this.write();
  }

  write() {
    this.patchGraphicsToForm();
    this.patchRegionsToForm();
    this.patchFeaturesToForm();

    let maxActiveUsersFormValue = this.companyForm.get("maxActiveUsers").value;

    if (maxActiveUsersFormValue) {
      let parsed = Number.parseInt(maxActiveUsersFormValue);
      this.companyForm.get("maxActiveUsers").patchValue(parsed);
    }
    else {
      this.companyForm.get("maxActiveUsers").patchValue(null);
    }

    let companyData = this.companyForm.getRawValue();
    this.requestService.writeCompany(companyData)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((next) => {
        this.navigateBack();
      });
  }
  // debug() {
  //   console.log(this.companyForm);
  // }

  navigateBack() {
    this.router.navigate(['company/list']);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
